import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import VendorProfileCard from '../components/VendorProfileCard';
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface VendorService {
  id: string;
  name: string;
  description?: string;
  hourlyRate?: number;
}

interface PropertyGroup {
  id: string;
  name: string;
  assignedAt: string;
}

interface Vendor {
  id: string;
  companyName: string;
  serviceType: string;
  contactEmail: string;
  contactPhone: string;
  description?: string;
  services: VendorService[];
  propertyGroups?: PropertyGroup[];
}

const Vendors: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [newVendor, setNewVendor] = useState<Omit<Vendor, 'id' | 'services'> & { password: string }>({
    companyName: '',
    serviceType: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    password: ''
  });
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [propertyGroups, setPropertyGroups] = useState<PropertyGroup[]>([]);
  const [showPropertyGroupManager, setShowPropertyGroupManager] = useState<string | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(user?.hoaId || null);

  useEffect(() => {
    const fetchVendors = async () => {
      if (!selectedGroupId || !user?.token) {
        console.log('Skipping vendor fetch - no selectedGroupId or token:', { selectedGroupId, hasToken: !!user?.token });
        return;
      }
      
      console.log('Fetching vendors for group:', selectedGroupId);
      try {
        const response = await api.get<Vendor[]>('/api/vendors', {
          params: { hoaId: selectedGroupId },
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });
        console.log('Vendors fetched successfully:', response.data);
        setVendors(response.data);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        // Fallback to empty array for clean UI
        setVendors([]);
      }
    };

    const fetchPropertyGroups = async () => {
      if (!user?.token || !isAdmin) return;
      
      try {
        const response = await api.get<PropertyGroup[]>('/api/property-groups', {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });
        setPropertyGroups(response.data);
        
        // If admin has no selectedGroupId, default to first property group
        if (!selectedGroupId && response.data.length > 0) {
          console.log('Setting default selectedGroupId for admin:', response.data[0].id);
          setSelectedGroupId(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching property groups:', error);
      }
    };

    fetchPropertyGroups();
    fetchVendors();
  }, [selectedGroupId, user?.token, isAdmin]);

  const handleCreateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = user?.token;
      if (!token) {
        console.error('No authentication token');
        return;
      }

      const response = await api.post<Vendor>('/api/vendors', {
        ...newVendor,
        hoaId: selectedGroupId
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setVendors(prevVendors => [...prevVendors, response.data]);
      
      // Generate the username for display
      const generatedUsername = newVendor.companyName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
      setSuccessMessage(`✅ Vendor created successfully! Login credentials: Username: "${generatedUsername}", Password: "${newVendor.password}"`);
      
      setNewVendor({
        companyName: '',
        serviceType: '',
        contactEmail: '',
        contactPhone: '',
        description: '',
        password: ''
      });
      
      // Clear success message after 10 seconds
      setTimeout(() => setSuccessMessage(''), 10000);
    } catch (error) {
      console.error('Error creating vendor:', error);
    }
  };



  const handleDeleteVendor = async (vendorId: string, vendorName: string) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${vendorName}"? This will permanently remove the vendor and their login account.`)) {
      return;
    }

    try {
      const token = user?.token;
      if (!token) {
        console.error('No authentication token');
        return;
      }

      await api.delete(`/api/vendors/${vendorId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setVendors(prevVendors => prevVendors.filter(vendor => vendor.id !== vendorId));
      setSuccessMessage(`✅ Vendor "${vendorName}" deleted successfully!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setSuccessMessage('❌ Failed to delete vendor. Please try again.');
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const handleAddVendorToPropertyGroup = async (vendorId: string, propertyGroupId: string) => {
    try {
      const token = user?.token;
      if (!token) return;

      await api.post(`/api/vendors/${vendorId}/property-groups`, {
        propertyGroupId
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Refresh vendors to get updated property group data
      const response = await api.get<Vendor[]>('/api/vendors', {
        params: { hoaId: selectedGroupId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setVendors(response.data);
      setSuccessMessage('✅ Vendor added to property group successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding vendor to property group:', error);
      setSuccessMessage('❌ Failed to add vendor to property group.');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleRemoveVendorFromPropertyGroup = async (vendorId: string, propertyGroupId: string) => {
    try {
      const token = user?.token;
      if (!token) return;

      await api.delete(`/api/vendors/${vendorId}/property-groups/${propertyGroupId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Refresh vendors to get updated property group data
      const response = await api.get<Vendor[]>('/api/vendors', {
        params: { hoaId: selectedGroupId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setVendors(response.data);
      setSuccessMessage('✅ Vendor removed from property group successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error removing vendor from property group:', error);
      setSuccessMessage('❌ Failed to remove vendor from property group.');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };



  // Transform vendors to match VendorProfileCard interface
  const transformedVendors = vendors.map(vendor => ({
    name: vendor.companyName,
    services: [vendor.serviceType, ...(vendor.services?.map(s => s.name) || [])],
    rating: 4.5 + Math.random() * 0.5, // Mock rating for demo
    reviews: Math.floor(Math.random() * 30) + 5, // Mock reviews for demo
    verified: true, // All listed vendors are verified
    logo: `https://via.placeholder.com/80/${Math.floor(Math.random() * 16777215).toString(16)}/ffffff?text=${vendor.companyName.charAt(0)}`,
    description: vendor.description || `Professional ${vendor.serviceType} services`,
    originalVendor: vendor,
    propertyGroups: vendor.propertyGroups || []
  }));

  const handleRequestQuote = (vendor: any) => {
    const originalVendor = vendor.originalVendor;
    const message = `
🏠 Quote Request for ${vendor.name}

Contact Information:
📧 Email: ${originalVendor.contactEmail}
📞 Phone: ${originalVendor.contactPhone}
🔧 Services: ${vendor.services.join(', ')}
⭐ Rating: ${vendor.rating.toFixed(1)} (${vendor.reviews} reviews)

We'll help you connect with this verified vendor!
    `.trim();
    
    alert(message);
  };

  // Resident view - focused on finding and contacting services
  if (user?.role === 'RESIDENT') {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-4">
              <UserGroupIcon className="w-6 h-6 text-[#22d3ee]" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Find Trusted Services
            </h1>
          </div>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Connect with approved professionals for all your home service needs
          </p>
        </div>

        {/* Service Categories */}
        <div className="floating-card">
          <h2 className="text-2xl font-bold text-white mb-6">Popular Service Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200 text-center group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-400/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-white font-medium">Landscaping</p>
              <p className="text-white/60 text-sm">5 vendors</p>
            </button>

            <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200 text-center group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <p className="text-white font-medium">Cleaning</p>
              <p className="text-white/60 text-sm">3 vendors</p>
            </button>

            <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200 text-center group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a1 1 0 01-1-1V9a1 1 0 011-1h1a2 2 0 100-4H4a1 1 0 01-1-1V5a1 1 0 011-1h3a1 1 0 001-1V2a2 2 0 114 0z" />
                </svg>
              </div>
              <p className="text-white font-medium">Handyman</p>
              <p className="text-white/60 text-sm">4 vendors</p>
            </button>

            <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200 text-center group">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-white font-medium">Electrical</p>
              <p className="text-white/60 text-sm">2 vendors</p>
            </button>
          </div>
        </div>

        {/* Featured Services */}
        <div className="floating-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Featured Services</h2>
            <button className="btn-secondary text-sm">View All</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {transformedVendors.slice(0, 6).map((vendor, index) => (
              <div key={index} className="relative group">
                <VendorProfileCard
                  vendor={vendor}
                  onRequestQuote={handleRequestQuote}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quick Contact */}
        <div className="floating-card">
          <h2 className="text-2xl font-bold text-white mb-6">Need Help Finding a Service?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-gradient-to-r from-[#22d3ee]/10 to-[#38bdf8]/10 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-3">Contact Property Management</h3>
              <p className="text-white/60 mb-4">Get personalized recommendations for your specific needs</p>
              <button className="btn-primary w-full">
                Contact Management
              </button>
            </div>
            
            <div className="p-6 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-3">Emergency Services</h3>
              <p className="text-white/60 mb-4">24/7 emergency contact for urgent repairs</p>
              <button className="btn-secondary w-full">
                Emergency Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin view (existing code)
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center animate-fade-in">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-4">
            <UserGroupIcon className="w-6 h-6 text-[#22d3ee]" />
          </div>
          <h1 className="text-4xl font-bold text-white">
            Approved Vendors
          </h1>
        </div>
        <p className="text-xl text-white/60 max-w-2xl mx-auto">
          Connect with trusted professionals who understand our community standards
        </p>
      </div>

      {/* Admin Section */}
      {isAdmin && (
        <div className="floating-card animate-slide-up">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-3">
              <PlusIcon className="w-5 h-5 text-[#22d3ee]" />
            </div>
            <h2 className="text-xl font-semibold text-white">Add New Vendor</h2>
          </div>
          
          {successMessage && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl backdrop-blur-sm">
              <p className="font-medium">{successMessage}</p>
              <p className="text-sm mt-1 text-green-300">Share these credentials with the vendor so they can access their portal.</p>
            </div>
          )}
          
          <form onSubmit={handleCreateVendor} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Company Name</label>
                <input
                  type="text"
                  value={newVendor.companyName}
                  onChange={(e) => setNewVendor({...newVendor, companyName: e.target.value})}
                  className="input-field w-full"
                  placeholder="Enter company name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Service Type</label>
                <input
                  type="text"
                  value={newVendor.serviceType}
                  onChange={(e) => setNewVendor({...newVendor, serviceType: e.target.value})}
                  className="input-field w-full"
                  placeholder="e.g., Landscaping, Plumbing"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Contact Email</label>
                <input
                  type="email"
                  value={newVendor.contactEmail}
                  onChange={(e) => setNewVendor({...newVendor, contactEmail: e.target.value})}
                  className="input-field w-full"
                  placeholder="vendor@company.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Contact Phone</label>
                <input
                  type="tel"
                  value={newVendor.contactPhone}
                  onChange={(e) => setNewVendor({...newVendor, contactPhone: e.target.value})}
                  className="input-field w-full"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Description</label>
              <textarea
                value={newVendor.description}
                onChange={(e) => setNewVendor({...newVendor, description: e.target.value})}
                className="input-field w-full"
                placeholder="Brief description of services offered..."
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Login Password</label>
              <input
                type="password"
                value={newVendor.password}
                onChange={(e) => setNewVendor({...newVendor, password: e.target.value})}
                className="input-field w-full"
                placeholder="Set password for vendor login"
                required
              />
              <p className="mt-2 text-sm text-white/60">
                This password will be used by the vendor to log in to their portal
              </p>
              {newVendor.companyName && (
                <p className="mt-2 text-sm text-[#22d3ee]">
                  <strong>Generated username:</strong> {newVendor.companyName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')}
                </p>
              )}
            </div>
            <button 
              type="submit" 
              className="btn-primary w-full"
            >
              Add Vendor
            </button>
          </form>
        </div>
      )}

      {/* Vendor Cards */}
      {vendors.length === 0 ? (
        <div className="floating-card text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
            <UserGroupIcon className="w-8 h-8 text-[#22d3ee]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Vendors Yet</h3>
          <p className="text-white/60">
            {isAdmin 
              ? "Add your first vendor using the form above to get started." 
              : "No vendors have been registered for this property group yet."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {transformedVendors.map((vendor, index) => (
            <div 
              key={vendor.originalVendor.id}
              className="relative"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <VendorProfileCard 
                vendor={vendor} 
                onRequestQuote={handleRequestQuote}
              />
              
              {/* Property Groups Display */}
              {vendor.propertyGroups && vendor.propertyGroups.length > 1 && (
                <div className="absolute top-4 left-4">
                  <div className="flex flex-wrap gap-1">
                    {vendor.propertyGroups.slice(0, 2).map((pg) => (
                      <span
                        key={pg.id}
                        className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-md"
                      >
                        {pg.name.length > 10 ? `${pg.name.substring(0, 10)}...` : pg.name}
                      </span>
                    ))}
                    {vendor.propertyGroups.length > 2 && (
                      <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-md">
                        +{vendor.propertyGroups.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Buttons */}
              {isAdmin && (
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => setShowPropertyGroupManager(
                      showPropertyGroupManager === vendor.originalVendor.id ? null : vendor.originalVendor.id
                    )}
                    className="w-8 h-8 bg-blue-500/20 hover:bg-blue-500 text-blue-400 hover:text-white rounded-lg transition-all duration-200 flex items-center justify-center"
                    title={`Manage property groups for ${vendor.originalVendor.companyName}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteVendor(vendor.originalVendor.id, vendor.originalVendor.companyName)}
                    className="w-8 h-8 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded-lg transition-all duration-200 flex items-center justify-center group"
                    title={`Remove ${vendor.originalVendor.companyName}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Property Group Manager Modal */}
              {isAdmin && showPropertyGroupManager === vendor.originalVendor.id && (
                <div className="absolute inset-0 bg-black/80 rounded-2xl z-10 p-6 overflow-y-auto">
                  <div className="bg-slate-800/90 backdrop-blur-sm rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">
                        Manage Property Groups for {vendor.originalVendor.companyName}
                      </h3>
                      <button
                        onClick={() => setShowPropertyGroupManager(null)}
                        className="w-6 h-6 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded transition-colors"
                      >
                        <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Current Property Groups */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-white/80 mb-2">Current Access:</h4>
                      <div className="space-y-2">
                        {vendor.propertyGroups && vendor.propertyGroups.length > 0 ? (
                          vendor.propertyGroups.map((pg) => (
                            <div key={pg.id} className="flex items-center justify-between bg-slate-700/50 p-3 rounded-lg">
                              <div>
                                <span className="text-white text-sm font-medium">{pg.name}</span>
                                <p className="text-white/60 text-xs">
                                  Added: {new Date(pg.assignedAt).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRemoveVendorFromPropertyGroup(vendor.originalVendor.id, pg.id)}
                                className="text-red-400 hover:text-red-300 transition-colors"
                                title="Remove access"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-white/60 text-sm">No property groups assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Add to Property Groups */}
                    <div>
                      <h4 className="text-sm font-medium text-white/80 mb-2">Add to Property Groups:</h4>
                      <div className="space-y-2">
                        {propertyGroups
                          .filter(pg => !vendor.propertyGroups?.some(vpg => vpg.id === pg.id))
                          .map((pg) => (
                            <div key={pg.id} className="flex items-center justify-between bg-slate-700/30 p-3 rounded-lg">
                              <span className="text-white text-sm">{pg.name}</span>
                              <button
                                onClick={() => handleAddVendorToPropertyGroup(vendor.originalVendor.id, pg.id)}
                                className="text-green-400 hover:text-green-300 transition-colors"
                                title="Add access"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        {propertyGroups.length === vendor.propertyGroups?.length && (
                          <p className="text-white/60 text-sm">Vendor has access to all available property groups</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Additional Info for Vendors */}
      {vendors.length > 0 && (
        <div className="floating-card">
          <h3 className="text-lg font-semibold text-white mb-4">For Vendors</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-white/80">
            <div>
              <h4 className="font-medium text-white mb-2">Manage Your Services</h4>
              <p>Log in to your vendor portal to add services, update rates, and manage your profile.</p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-2">Get More Bookings</h4>
              <p>Keep your profile updated and respond quickly to quote requests to build your reputation.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors; 